import apiClient from '../api/api';

export interface DocumentCompareRequest {
  source_file_id: string;
  target_file_id: string;
  schema_id?: string;
  instructions?: string;
  ai_performance_level: string;
}

export interface DocumentCompareResponse {
  success: boolean;
  code: number;
  message?: string;
  data?: any;
}

export interface DocumentCompareListRequest {
  skip: number;
  limit: number;
  document_store_id: string;
}

export interface DocumentCompareListResponse {
  success: boolean;
  code: number;
  data: {
    total_count: number;
    documentCompareDatasList: any[];
    last_modified: string;
  };
}

export interface DocumentCompareDetailResponse {
  success: boolean;
  code: number;
  data: any;
}

export interface DocumentCompareStatusRequest {
  document_compare_ids: string[];
  document_store_id: string;
}

export interface DocumentCompareStatusResponse {
  success: boolean;
  code: number;
  data: Array<{
    _id: string;
    document_compare_status: string;
    id: string;
  }>;
}

// Type for the data object in response
export interface DocumentDownloadData {
  name: string;
  key: string;
  url: string;
  fileType: string;
}

// Type for the full API response
export interface DocumentDownloadResponse {
  success: boolean;
  code: number;
  data: DocumentDownloadData;
}


class DocumentCompareService {
  // POST: Compare two documents
  async compareDocuments(
    documentStoreId: string,
    data: DocumentCompareRequest
  ): Promise<DocumentCompareResponse> {
    try {
      const response = await apiClient.post(
        `/dms-v2-srv/document-compare-srv/document-compare?document_store_id=${documentStoreId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error comparing documents:', error);
      throw error;
    }
  }

  // POST: Get list of document comparisons
  async getDocumentCompareList(
    data: DocumentCompareListRequest
  ): Promise<DocumentCompareListResponse> {
    try {
      const response = await apiClient.post(
        '/dms-v2-srv/document-compare-data-srv/utils/list',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching document compare list:', error);
      throw error;
    }
  }

  // GET: Get document comparison by ID
  async getDocumentCompareById(
    compareId: string
  ): Promise<DocumentCompareDetailResponse> {
    try {
      const response = await apiClient.get(
        `/dms-v2-srv/document-compare-data-srv/${compareId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching document compare details:', error);
      throw error;
    }
  }

  // POST: Get status by IDs for polling
  async getStatusByIds(
    data: DocumentCompareStatusRequest
  ): Promise<DocumentCompareStatusResponse> {
    try {
      const response = await apiClient.post(
        '/dms-v2-srv/document-compare-data-srv/utils/get-status-by-ids',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching document compare statuses:', error);
      throw error;
    }
  }

  async getDownloadLink(
  data: any
): Promise<DocumentDownloadResponse> {
    try {
      const response = await apiClient.post(
        '/dms-v2-srv/document-compare-srv/download-pdf-without-template',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching document compare statuses:', error);
      throw error;
    }
  }

  // GET: Toggle dislike for a difference
  async toggleDislike(
    documentStoreId: string,
    differenceId: number
  ): Promise<any> {
    try {
      const response = await apiClient.get(
        `/dms-v2-srv/document-compare-data-srv/differences/toggle-disliked/${documentStoreId}/${differenceId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error toggling dislike:', error);
      throw error;
    }
  }
}

export const documentCompareService = new DocumentCompareService();
export default documentCompareService;
